import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, ChefHat, Wallet, CreditCard, CheckCircle2, X, AlertTriangle, Plus, Loader2 } from "lucide-react";

const QUICK_AMOUNTS = [200, 500, 1000, 2000];

export default function ParentPreorder({ embedded = false }) {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [scheduledDate, setScheduledDate] = useState(() => {
    const d = new Date(Date.now() + 86400000);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  // Payment modal state
  const [payModal, setPayModal] = useState({ open: false, plan: null });
  const [placing, setPlacing] = useState(false);
  const [invoice, setInvoice] = useState(null); // after success
  // Top-up modal state
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  const [topupBusy, setTopupBusy] = useState(false);

  useEffect(() => {
    api.get("/meal-plans").then(r => {
      const daily = (r.data || []).filter(p => p.type === "daily" && p.active !== false);
      setPlans(daily);
    });
    if (user?.role === "parent") {
      api.get("/parent/children").then(r => {
        setChildren(r.data);
        if (r.data.length) setStudentId(r.data[0].id);
      });
    } else {
      setStudentId(user?.id);
      setChildren([{ id: user?.id, name: user?.name, grade: user?.grade, wallet_balance: user?.wallet_balance || 0, is_subscriber: false, active_plan: null }]);
    }
  }, [user]);

  const child = children.find(c => c.id === studentId);
  const walletBalance = child?.wallet_balance || 0;

  const doTopup = async () => {
    if (!studentId || !topupAmount || topupAmount <= 0) return;
    setTopupBusy(true);
    try {
      const cfg = await api.get("/payments/cashfree/config").catch(() => ({ data: { cashfree_configured: false } }));
      const { data: topupData } = await api.post("/payments/cashfree/create-order", {
        amount: Number(topupAmount), target_user_id: studentId,
      });
      if (cfg.data.cashfree_configured && !topupData.is_mock && window.Cashfree && topupData.payment_session_id) {
        const cf = window.Cashfree({ mode: cfg.data.mode || "sandbox" });
        const result = await cf.checkout({ paymentSessionId: topupData.payment_session_id, redirectTarget: "_modal" });
        if (result.error) throw new Error(result.error.message || "Payment cancelled");
      } else {
        await api.post(`/payments/cashfree/mock-success/${topupData.cf_order_id}`);
      }
      toast.success(`₹${topupAmount} added to wallet`);
      setShowTopup(false);
      // Refresh child wallet balance
      if (user?.role === "parent") {
        const { data } = await api.get("/parent/children");
        setChildren(data);
      } else if (refresh) {
        await refresh();
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTopupBusy(false);
    }
  };

  const openPayment = (plan) => {
    const taxRate = plan.tax_rate || 5;
    const taxAmount = Math.round(plan.price * taxRate) / 100;
    const total = plan.price + taxAmount;
    setPayModal({ open: true, plan, taxRate, taxAmount, total });
    setInvoice(null);
  };

  const payFromWallet = async () => {
    const plan = payModal.plan;
    if (!plan || !studentId) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/meal-plans/subscribe", {
        student_id: studentId,
        plan_id: plan.id,
        start_date: scheduledDate,
        end_date: scheduledDate,
      });
      // Build invoice
      setInvoice({
        plan_name: plan.name,
        subtotal: plan.price,
        tax_rate: payModal.taxRate,
        tax_amount: payModal.taxAmount,
        total: payModal.total,
        date: scheduledDate,
        student_name: child?.name,
        payment_method: "Wallet",
        status: "Paid",
        ref_id: data.student_plan?.id || "—",
      });
      toast.success(`${plan.name} pre-ordered for ${scheduledDate}!`);
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setPlacing(false);
  };

  const topUpAndPay = async () => {
    const plan = payModal.plan;
    if (!plan || !studentId) return;
    setPlacing(true);
    try {
      const totalWithTax = payModal.total;
      // Create top-up order for total amount (price + tax)
      const { data: topupData } = await api.post("/payments/cashfree/create-order", {
        amount: totalWithTax,
        target_user_id: studentId,
      });
      // In mock mode, simulate success
      const cfg = await api.get("/payments/config");
      if (cfg.data.cashfree_configured && !topupData.is_mock && window.Cashfree && topupData.payment_session_id) {
        const cf = window.Cashfree({ mode: cfg.data.env === "PRODUCTION" ? "production" : "sandbox" });
        const result = await cf.checkout({ paymentSessionId: topupData.payment_session_id, redirectTarget: "_modal" });
        if (result.error) { toast.error(result.error.message || "Payment cancelled"); setPlacing(false); return; }
      } else {
        // Mock success
        await api.post(`/payments/cashfree/mock-success/${topupData.cf_order_id}`);
      }
      toast.success(`₹${payModal.total} added to wallet`);
      // Now subscribe
      const { data } = await api.post("/meal-plans/subscribe", {
        student_id: studentId,
        plan_id: plan.id,
        start_date: scheduledDate,
        end_date: scheduledDate,
      });
      setInvoice({
        plan_name: plan.name,
        subtotal: plan.price,
        tax_rate: payModal.taxRate,
        tax_amount: payModal.taxAmount,
        total: payModal.total,
        date: scheduledDate,
        student_name: child?.name,
        payment_method: "Cashfree → Wallet",
        status: "Paid",
        ref_id: data.student_plan?.id || "—",
      });
      toast.success(`${plan.name} pre-ordered for ${scheduledDate}!`);
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setPlacing(false);
  };

  return (
    <ConditionalShell embedded={embedded} role={user?.role}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Pre-order</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Tomorrow's meal, sorted today</h1>
            <p className="text-sm text-ink-500 mt-1">Pick a daily plan for the date you want. One per slot per day.</p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            {user?.role === "parent" && children.length > 0 && (
              <select data-testid="select-child" value={studentId || ""} onChange={(e) => setStudentId(e.target.value)}
                className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-semibold focus:border-brand focus:outline-none">
                {children.map(c => <option key={c.id} value={c.id}>{c.name} · {c.grade}</option>)}
              </select>
            )}
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5">
              <Calendar size={14} className="text-ink-500" />
              <input data-testid="schedule-date" type="date" value={scheduledDate}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (new Date(v + "T00:00:00").getDay() === 0) { toast.error("Pre-orders not accepted for Sundays."); return; }
                  setScheduledDate(v);
                }}
                className="text-sm font-semibold focus:outline-none" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="text-xs text-ink-500">Cut-off 4 PM IST · no Sunday pre-orders · full amount charged</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border text-sm">
              <Wallet size={14} className="text-brand" />
              <span className="font-bold text-ink">{inr(walletBalance)}</span>
              <span className="text-ink-500 text-xs">balance</span>
            </div>
            <button data-testid="preorder-topup-btn" onClick={() => setShowTopup(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand text-white text-sm font-semibold hover:bg-brand-600">
              <Plus size={14} /> Top up wallet
            </button>
          </div>
        </div>

        {/* Daily plans grid */}
        {plans.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <ChefHat size={32} className="mx-auto text-ink-400 mb-3" />
            <div className="font-heading text-xl font-bold text-ink">No daily plans available</div>
            <p className="text-ink-500 text-sm mt-2">The school hasn't configured daily pre-order plans yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="preorder-plans">
            {plans.map(plan => (
              <div key={plan.id} className="bg-white rounded-3xl border border-border overflow-hidden shadow-soft hover:shadow-medium transition-shadow flex flex-col" data-testid={`preorder-plan-${plan.id}`}>
                {plan.image_url ? (
                  <div className="h-40 overflow-hidden">
                    <img src={plan.image_url} alt={plan.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-40 bg-cream-200 flex items-center justify-center"><ChefHat size={40} className="text-ink-400" /></div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold uppercase">
                      {(plan.slots || ["lunch"])[0]}
                    </span>
                    {plan.non_veg_option && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold">NON-VEG</span>}
                  </div>
                  <div className="font-heading text-xl font-extrabold text-ink">{plan.name}</div>
                  <p className="text-sm text-ink-600 mt-1 flex-1">{plan.description || "One meal for the selected date."}</p>
                  {plan.nutrition && (
                    <div className="mt-2 text-[11px] text-ink-500 bg-cream-100 rounded-lg px-3 py-1.5">
                      {plan.nutrition.calories} kcal · {plan.nutrition.protein}g protein · {plan.nutrition.carbs}g carbs · {plan.nutrition.fat}g fat
                      {plan.nutrition.serving_size && <span className="ml-1">· {plan.nutrition.serving_size}</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="font-heading text-2xl font-extrabold text-ink">{inr(plan.price)}</div>
                    <button
                      data-testid={`preorder-btn-${plan.id}`}
                      onClick={() => openPayment(plan)}
                      className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95"
                    >
                      Pre-order
                    </button>
                  </div>
                  <div className="text-[10px] text-ink-500 mt-2 text-right">For {scheduledDate}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {payModal.open && payModal.plan && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-tall overflow-hidden">
            {invoice ? (
              /* Invoice / Receipt */
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Order confirmed</div>
                  <button onClick={() => { setPayModal({ open: false, plan: null }); setInvoice(null); }} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
                </div>
                <div className="text-center mb-6">
                  <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
                  <div className="font-heading text-2xl font-extrabold text-ink">{inr(invoice.total)}</div>
                  <div className="text-sm text-ink-600 mt-1">{invoice.plan_name}</div>
                </div>
                <div className="bg-cream-100 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-ink-500">Student</span><span className="font-semibold text-ink">{invoice.student_name}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Date</span><span className="font-semibold text-ink">{invoice.date}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Subtotal</span><span className="font-semibold text-ink">{inr(invoice.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">GST ({invoice.tax_rate}%)</span><span className="font-semibold text-ink">{inr(invoice.tax_amount)}</span></div>
                  <div className="flex justify-between border-t border-border pt-2"><span className="font-bold text-ink">Total paid</span><span className="font-bold text-ink">{inr(invoice.total)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Payment</span><span className="font-semibold text-ink">{invoice.payment_method}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Status</span><span className="font-semibold text-emerald-700">{invoice.status}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Reference</span><span className="font-mono text-xs text-ink-600">{invoice.ref_id}</span></div>
                </div>
                <button onClick={() => { setPayModal({ open: false, plan: null }); setInvoice(null); }}
                  className="mt-5 w-full px-5 py-3 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">
                  Done
                </button>
              </div>
            ) : (
              /* Payment options */
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-brand">Confirm pre-order</div>
                  <button onClick={() => setPayModal({ open: false, plan: null })} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
                </div>
                <div className="text-center mb-5">
                  <div className="font-heading text-xl font-extrabold text-ink">{payModal.plan.name}</div>
                  <div className="text-sm text-ink-500 mt-1">{child?.name} · {scheduledDate}</div>
                </div>

                {/* Price breakdown */}
                <div className="bg-cream-100 rounded-xl p-4 mb-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-ink-500">Subtotal</span><span className="font-semibold text-ink">{inr(payModal.plan.price)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">GST ({payModal.taxRate}%)</span><span className="font-semibold text-ink">{inr(payModal.taxAmount)}</span></div>
                  <div className="flex justify-between border-t border-border pt-2"><span className="font-bold text-ink">Total</span><span className="font-heading text-xl font-extrabold text-ink">{inr(payModal.total)}</span></div>
                </div>

                <div className="bg-cream-100 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Wallet size={16} className="text-brand" />
                      <span className="text-ink-700">Wallet balance</span>
                    </div>
                    <span className="font-heading font-extrabold text-ink">{inr(walletBalance)}</span>
                  </div>
                </div>

                {walletBalance >= payModal.total ? (
                  /* Sufficient balance */
                  <button data-testid="pay-wallet-btn" onClick={payFromWallet} disabled={placing}
                    className="w-full px-5 py-3.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                    <Wallet size={16} />
                    {placing ? "Processing..." : `Pay ${inr(payModal.total)} from wallet`}
                  </button>
                ) : (
                  /* Insufficient balance */
                  <>
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-sm text-amber-800">
                      <AlertTriangle size={16} />
                      <span>Insufficient balance. You need <b>{inr(payModal.total - walletBalance)}</b> more.</span>
                    </div>
                    <button data-testid="topup-pay-btn" onClick={topUpAndPay} disabled={placing}
                      className="w-full px-5 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                      <CreditCard size={16} />
                      {placing ? "Processing..." : `Top up ${inr(payModal.total)} & pay via Cashfree`}
                    </button>
                  </>
                )}

                <div className="mt-3 text-center text-[11px] text-ink-500">
                  {walletBalance >= payModal.total ? "Amount will be deducted from wallet instantly." : "Redirects to Cashfree for payment. Wallet credited, then plan purchased."}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top up wallet modal */}
      {showTopup && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => !topupBusy && setShowTopup(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-6 w-full max-w-md shadow-tall"
            data-testid="preorder-topup-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-brand/10 text-brand"><Wallet size={18} /></div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">Top up wallet</div>
                  <div className="font-heading text-xl font-extrabold text-ink">{child?.name || "Wallet"}</div>
                </div>
              </div>
              <button onClick={() => setShowTopup(false)} disabled={topupBusy} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
            </div>
            <div className="bg-cream-100 rounded-2xl p-4 mb-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Current balance</div>
              <div className="font-heading text-3xl font-extrabold text-ink mt-1">{inr(walletBalance)}</div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Amount to add (₹)</label>
              <input data-testid="preorder-topup-amount" type="number" min={50} value={topupAmount}
                onChange={e => setTopupAmount(Math.max(50, Number(e.target.value) || 0))}
                className="mt-1 w-full rounded-xl border border-border px-4 py-3 text-lg font-bold focus:border-brand focus:outline-none" />
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {QUICK_AMOUNTS.map(a => (
                <button key={a} data-testid={`preorder-topup-quick-${a}`} onClick={() => setTopupAmount(a)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold ${topupAmount === a ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
                  ₹{a}
                </button>
              ))}
            </div>
            <button data-testid="preorder-topup-pay" onClick={doTopup} disabled={topupBusy || topupAmount <= 0}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-50">
              {topupBusy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {topupBusy ? "Processing…" : `Add ${inr(topupAmount)}`}
            </button>
            <div className="mt-3 text-center text-[11px] text-ink-500">Redirects to Cashfree (mocked in this demo). Balance updates instantly.</div>
          </div>
        </div>
      )}
    </ConditionalShell>
  );
}

const ConditionalShell = ({ embedded, role, children }) => embedded
  ? <>{children}</>
  : <AppShell variant={role === "student" ? "student" : "parent"}>{children}</AppShell>;
